import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  CheckCircle2,
  Clock,
  Cpu,
  Database,
  FileText,
  Filter,
  GitBranch,
  RefreshCcw,
  Search,
  Settings2,
  ShieldCheck,
} from "lucide-react";
import { hbmSocialApi } from "@/lib/hbmSocial";

const STAGE_OPTIONS = [
  "intent_router",
  "grounding",
  "decision_frame",
  "state_delta_provenance",
  "structural_pass",
  "lens_router",
  "hypothesis_competition",
  "critical_unknowns",
  "interaction_strategy",
  "question_audit",
  "synthesis_audit",
  "final_synthesis",
];

function valueText(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value, null, 2);
}

function labelize(value) {
  return valueText(value).replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function TraceBlock({ icon: Icon, title, children, testId }) {
  return (
    <section className="hbm-trace-block" data-testid={testId}>
      <header><Icon size={16} /><span>{title}</span></header>
      {children}
    </section>
  );
}

function TraceRow({ label, value, testId }) {
  return <p data-testid={testId}><b>{label}</b><span>{valueText(value)}</span></p>;
}

function StageBadge({ children, tone = "neutral", testId }) {
  return <span className={`hbm-stage-badge ${tone}`} data-testid={testId}>{children}</span>;
}

function StageCard({ stage, index, selected }) {
  const issues = stage?.issues || [];
  const tokens = [stage?.input_tokens, stage?.output_tokens].filter((x) => x !== null && x !== undefined).join(" / ");
  return (
    <details className="hbm-stage-card" open={index === 0} data-testid={`reasoning-stage-card-${index}`}>
      <summary data-testid={`reasoning-stage-summary-${index}`}>
        <span className="hbm-stage-index" data-testid={`reasoning-stage-index-${index}`}>{String(index + 1).padStart(2, "0")}</span>
        <strong data-testid={`reasoning-stage-name-${index}`}>{labelize(stage?.stage)}</strong>
        <StageBadge tone={stage?.deterministic ? "neutral" : "provider"} testId={`reasoning-stage-provider-badge-${index}`}>
          {stage?.deterministic ? "deterministic" : valueText(stage?.provider)}
        </StageBadge>
        {stage?.fallback_used && <StageBadge tone="warn" testId={`reasoning-stage-fallback-badge-${index}`}>fallback</StageBadge>}
        {!!issues.length && <StageBadge tone="issue" testId={`reasoning-stage-issue-badge-${index}`}>{issues.length} issue{issues.length === 1 ? "" : "s"}</StageBadge>}
        <small data-testid={`reasoning-stage-latency-${index}`}>{valueText(stage?.latency_ms)} ms</small>
      </summary>
      <div className="hbm-stage-grid" data-testid={`reasoning-stage-detail-${index}`}>
        <TraceRow label="Provider" value={stage?.provider || (stage?.deterministic ? "deterministic" : null)} testId={`reasoning-stage-provider-${index}`} />
        <TraceRow label="Model" value={stage?.model} testId={`reasoning-stage-model-${index}`} />
        <TraceRow label="Confidence" value={stage?.confidence} testId={`reasoning-stage-confidence-${index}`} />
        <TraceRow label="Fallback reason" value={stage?.fallback_reason} testId={`reasoning-stage-fallback-reason-${index}`} />
        <TraceRow label="Tokens in / out" value={tokens || null} testId={`reasoning-stage-tokens-${index}`} />
        <TraceRow label="Issues" value={issues} testId={`reasoning-stage-issues-${index}`} />
        {stage?.stage === "critical_unknowns" && (
          <TraceRow label="Selected unknown" value={selected?.selectedUnknownId} testId={`reasoning-stage-selected-unknown-${index}`} />
        )}
        {stage?.stage === "decision_frame" && (
          <TraceRow label="Decision frame" value={selected?.decisionFrame} testId={`reasoning-stage-decision-frame-${index}`} />
        )}
      </div>
    </details>
  );
}

function InspectorSummary({ selected }) {
  const stages = selected?.stageTrace || [];
  const providerStages = stages.filter((s) => !s?.deterministic).length;
  const fallbackStages = stages.filter((s) => s?.fallback_used).length;
  const issueStages = stages.filter((s) => (s?.issues || []).length).length;
  return (
    <div className="hbm-inspector-summary" data-testid="reasoning-inspector-summary">
      <article data-testid="reasoning-inspector-stage-count"><b>{stages.length}</b><span>stages</span></article>
      <article data-testid="reasoning-inspector-provider-stage-count"><b>{providerStages}</b><span>provider calls</span></article>
      <article data-testid="reasoning-inspector-fallback-count"><b>{fallbackStages}</b><span>fallbacks</span></article>
      <article data-testid="reasoning-inspector-issue-count"><b>{issueStages}</b><span>issue stages</span></article>
    </div>
  );
}

function ReasoningChainInspector({ selected }) {
  const stages = selected?.stageTrace || [];
  return (
    <TraceBlock icon={ShieldCheck} title="Reasoning Chain Inspector" testId="reasoning-chain-inspector">
      <InspectorSummary selected={selected} />
      <div className="hbm-inspector-grid" data-testid="reasoning-inspector-key-fields">
        <TraceRow label="Initial situation hash" value={selected?.initialSituationHash} testId="reasoning-inspector-initial-hash" />
        <TraceRow label="Current turn intent" value={selected?.currentTurnIntent} testId="reasoning-inspector-turn-intent" />
        <TraceRow label="Strategy" value={selected?.strategy} testId="reasoning-inspector-strategy" />
        <TraceRow label="Selected unknown" value={selected?.selectedUnknownId} testId="reasoning-inspector-selected-unknown" />
        <TraceRow label="Question score" value={selected?.selectedQuestionScore ?? selected?.questionScore} testId="reasoning-inspector-question-score" />
        <TraceRow label="Grounding confidence" value={selected?.groundingConfidence} testId="reasoning-inspector-grounding-confidence" />
        <TraceRow label="Frame confidence" value={selected?.frameConfidence} testId="reasoning-inspector-frame-confidence" />
        <TraceRow label="Overall uncertainty" value={selected?.overallUncertainty} testId="reasoning-inspector-overall-uncertainty" />
        <TraceRow label="Coherence pass" value={selected?.coherenceCheckPass} testId="reasoning-inspector-coherence-pass" />
        <TraceRow label="Audit pass" value={selected?.auditPass} testId="reasoning-inspector-audit-pass" />
        <TraceRow label="Repair used" value={selected?.repairUsed} testId="reasoning-inspector-repair-used" />
        <TraceRow label="Answer provider/model" value={`${valueText(selected?.answerActualProvider || selected?.provider)} / ${valueText(selected?.answerActualModel || selected?.model)}`} testId="reasoning-inspector-answer-provider" />
      </div>
      <details className="hbm-inspector-assumptions" data-testid="reasoning-inspector-blocked-assumptions-details">
        <summary data-testid="reasoning-inspector-blocked-assumptions-summary">Blocked assumptions & audit issues</summary>
        <TraceRow label="Blocked assumptions" value={selected?.unsupportedAssumptionsBlocked} testId="reasoning-inspector-blocked-assumptions" />
        <TraceRow label="Audit issues" value={selected?.auditIssues} testId="reasoning-inspector-audit-issues" />
        <TraceRow label="Coherence issues" value={selected?.coherenceIssues} testId="reasoning-inspector-coherence-issues" />
      </details>
      <div className="hbm-stage-timeline" data-testid="reasoning-stage-timeline">
        {stages.map((stage, index) => <StageCard key={`${stage?.stage || "stage"}-${index}`} stage={stage} index={index} selected={selected} />)}
        {!stages.length && <p data-testid="reasoning-stage-empty">No stage trace captured for this response.</p>}
      </div>
    </TraceBlock>
  );
}

export function HBMResponseTrace() {
  const [traces, setTraces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ situation_id: "", provider: "", stage: "", fallback_used: "", issue: "" });

  const providers = useMemo(() => {
    const names = new Set();
    traces.forEach((trace) => {
      [trace.provider, trace.answerActualProvider, ...(trace.stageTrace || []).map((s) => s?.provider)].filter(Boolean).forEach((name) => names.add(name));
    });
    return Array.from(names).sort();
  }, [traces]);

  async function load(nextFilters = filters) {
    setLoading(true);
    setError("");
    try {
      const serverFilters = { ...nextFilters };
      if (serverFilters.fallback_used === "") delete serverFilters.fallback_used;
      const list = await hbmSocialApi.listResponseTraces(50, serverFilters);
      const rows = list.traces || [];
      setTraces(rows);
      if (rows.length) {
        const detail = await hbmSocialApi.getResponseTrace(rows[0].request_id);
        setSelected(detail.trace);
      } else {
        setSelected(null);
      }
    } catch (cause) {
      setError(cause?.message || "Unable to load response traces.");
    } finally {
      setLoading(false);
    }
  }

  async function selectTrace(requestId) {
    setLoading(true);
    setError("");
    try {
      const detail = await hbmSocialApi.getResponseTrace(requestId);
      setSelected(detail.trace);
    } catch (cause) {
      setError(cause?.message || "Unable to open response trace.");
    } finally {
      setLoading(false);
    }
  }

  function updateFilter(key, value) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function resetFilters() {
    const next = { situation_id: "", provider: "", stage: "", fallback_used: "", issue: "" };
    setFilters(next);
    load(next);
  }

  useEffect(() => { load(filters); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="hbm-trace" data-testid="response-trace-container">
      <aside className="hbm-social-card hbm-trace-list" data-testid="response-trace-list">
        <header><span>RESPONSE TRACE</span><b>{loading ? "Loading" : `${traces.length} recent`}</b></header>
        <div className="hbm-trace-filters" data-testid="response-trace-filters">
          <label data-testid="response-trace-filter-situation-label"><Search size={13} /> Situation ID
            <input value={filters.situation_id} onChange={(event) => updateFilter("situation_id", event.target.value)} placeholder="hbm-sit-…" data-testid="response-trace-filter-situation-input" />
          </label>
          <label data-testid="response-trace-filter-stage-label"><Filter size={13} /> Stage
            <select value={filters.stage} onChange={(event) => updateFilter("stage", event.target.value)} data-testid="response-trace-filter-stage-select">
              <option value="">All stages</option>
              {STAGE_OPTIONS.map((stage) => <option key={stage} value={stage}>{labelize(stage)}</option>)}
            </select>
          </label>
          <label data-testid="response-trace-filter-provider-label"><Cpu size={13} /> Provider
            <select value={filters.provider} onChange={(event) => updateFilter("provider", event.target.value)} data-testid="response-trace-filter-provider-select">
              <option value="">All providers</option>
              {providers.map((provider) => <option key={provider} value={provider}>{provider}</option>)}
            </select>
          </label>
          <label data-testid="response-trace-filter-fallback-label"><AlertTriangle size={13} /> Fallback
            <select value={filters.fallback_used} onChange={(event) => updateFilter("fallback_used", event.target.value)} data-testid="response-trace-filter-fallback-select">
              <option value="">Any</option>
              <option value="true">Fallback used</option>
              <option value="false">No fallback</option>
            </select>
          </label>
          <label data-testid="response-trace-filter-issue-label"><ShieldCheck size={13} /> Issue contains
            <input value={filters.issue} onChange={(event) => updateFilter("issue", event.target.value)} placeholder="UNSUPPORTED_CONTEXT" data-testid="response-trace-filter-issue-input" />
          </label>
          <div className="hbm-trace-filter-actions" data-testid="response-trace-filter-actions">
            <button type="button" onClick={() => load(filters)} disabled={loading} data-testid="response-trace-filter-apply-button"><Filter size={14} /> Apply</button>
            <button type="button" onClick={resetFilters} disabled={loading} data-testid="response-trace-filter-reset-button"><RefreshCcw size={14} /> Reset</button>
          </div>
        </div>
        {error && <p className="hbm-trace-error" data-testid="response-trace-error"><AlertTriangle size={14} /> {error}</p>}
        <div data-testid="response-trace-list-items">
          {traces.map((trace) => (
            <button key={trace.request_id} type="button" className={selected?.request_id === trace.request_id ? "active" : ""} onClick={() => selectTrace(trace.request_id)} data-testid={`response-trace-list-item-${trace.request_id}`}>
              <b>{trace.request_id}</b>
              <span>{trace.answerActualProvider || trace.provider || "provider"} · {trace.answerActualModel || trace.model || "model"}</span>
              <small>{trace.stageTrace?.length || 0} stages · {trace.timestamp || trace.created_at}</small>
            </button>
          ))}
        </div>
        {!traces.length && !loading && <p data-testid="response-trace-empty-state">No response traces yet.</p>}
      </aside>

      {selected && (
        <article className="hbm-social-card hbm-trace-detail" data-testid="response-trace-detail">
          <header><span>PRIVATE REASONING TRACE</span><b>{selected.request_id}</b></header>
          <ReasoningChainInspector selected={selected} />
          <ArrowDown className="hbm-trace-arrow" size={18} />
          <TraceBlock icon={FileText} title="User Query" testId="response-trace-user-query">
            <pre>{selected.user_query}</pre>
          </TraceBlock>
          <ArrowDown className="hbm-trace-arrow" size={18} />
          <TraceBlock icon={GitBranch} title="HBM Classification" testId="response-trace-classification">
            <TraceRow label="Mode" value={selected.mode} testId="response-trace-mode-row" />
            <TraceRow label="Domain" value={selected.hbm_classification?.domain} testId="response-trace-domain-row" />
            <TraceRow label="Primary lens" value={selected.hbm_classification?.primary_lens} testId="response-trace-primary-lens-row" />
            <TraceRow label="Secondary lenses" value={selected.hbm_classification?.secondary_lenses} testId="response-trace-secondary-lenses-row" />
            <TraceRow label="Evidence policy" value={selected.hbm_classification?.evidence_policy} testId="response-trace-evidence-policy-row" />
            <TraceRow label="Dynamic k" value={selected.hbm_classification?.dynamic_k} testId="response-trace-dynamic-k-row" />
          </TraceBlock>
          <ArrowDown className="hbm-trace-arrow" size={18} />
          <TraceBlock icon={Database} title="Activated HBM Modules & Retrieved Corpus Entries" testId="response-trace-retrieval">
            <TraceRow label="Retrieval enabled" value={selected.retrieval_enabled} testId="response-trace-retrieval-enabled-row" />
            <TraceRow label="Retrieved IDs" value={selected.retrieved_item_ids} testId="response-trace-retrieved-ids-row" />
            <TraceRow label="Retrieved scores" value={selected.retrieved_item_scores} testId="response-trace-retrieved-scores-row" />
            <TraceRow label="Reasoning modules" value={(selected.reasoning_modules || []).map((m) => `${m.id} · ${m.class_name || m.group || m.lens}`)} testId="response-trace-reasoning-modules-row" />
          </TraceBlock>
          <ArrowDown className="hbm-trace-arrow" size={18} />
          <TraceBlock icon={Cpu} title="Provider & Exact Model" testId="response-trace-provider">
            <TraceRow label="Provider" value={selected.answerActualProvider || selected.provider} testId="response-trace-provider-row" />
            <TraceRow label="Model" value={selected.answerActualModel || selected.model} testId="response-trace-model-row" />
            <TraceRow label="Provider request ID" value={selected.provider_request_id} testId="response-trace-provider-request-row" />
            <TraceRow label="Fallback used" value={selected.fallbackTriggered ?? selected.fallback_used} testId="response-trace-fallback-row" />
          </TraceBlock>
          <ArrowDown className="hbm-trace-arrow" size={18} />
          <TraceBlock icon={Settings2} title="Inference Configuration" testId="response-trace-config">
            <TraceRow label="Temperature" value={selected.temperature} testId="response-trace-temperature-row" />
            <TraceRow label="Top-p" value={selected.top_p} testId="response-trace-top-p-row" />
            <TraceRow label="Max tokens" value={selected.max_tokens} testId="response-trace-max-tokens-row" />
            <TraceRow label="Input tokens" value={selected.input_tokens} testId="response-trace-input-tokens-row" />
            <TraceRow label="Output tokens" value={selected.output_tokens} testId="response-trace-output-tokens-row" />
            <TraceRow label="Cached tokens" value={selected.cached_tokens} testId="response-trace-cached-tokens-row" />
            <TraceRow label="Latency ms" value={selected.latency} testId="response-trace-latency-row" />
          </TraceBlock>
          <ArrowDown className="hbm-trace-arrow" size={18} />
          <TraceBlock icon={Clock} title="Prompt Construction & Post-processing" testId="response-trace-post-processing">
            <TraceRow label="System prompt version" value={selected.system_prompt_version} testId="response-trace-system-version-row" />
            <TraceRow label="Dynamic prompt version" value={selected.dynamic_prompt_version} testId="response-trace-dynamic-version-row" />
            <TraceRow label="Post-processing" value={selected.post_processing_steps} testId="response-trace-post-processing-row" />
            <TraceRow label="Errors / fallback events" value={selected.error_fallback_events} testId="response-trace-errors-row" />
            <details data-testid="response-trace-system-prompt-details">
              <summary data-testid="response-trace-system-prompt-summary">System-level HBM instructions</summary>
              <pre>{selected.system_prompt}</pre>
            </details>
            <details data-testid="response-trace-dynamic-prompt-details">
              <summary data-testid="response-trace-dynamic-prompt-summary">Dynamic prompt/context</summary>
              <pre>{selected.dynamic_prompt}</pre>
            </details>
          </TraceBlock>
          <ArrowDown className="hbm-trace-arrow" size={18} />
          <TraceBlock icon={CheckCircle2} title="Final Response" testId="response-trace-final-response">
            <pre>{valueText(selected.final_response)}</pre>
            <TraceRow label="Visible section sources" value={selected.visible_section_sources} testId="response-trace-visible-sources-row" />
          </TraceBlock>
        </article>
      )}
    </section>
  );
}